import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogService, BlogPost } from '../../services/blogService';

type FilterTab = 'all' | 'story' | 'news';

const PostsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchPosts = async (tab: FilterTab, page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const postType = tab === 'all' ? undefined : tab;
      const response = await blogService.fetchAdminPosts(page, 20, postType);
      setPosts(response.data);
      setPagination({
        page: response.pagination.page,
        pages: response.pagination.pages,
        total: response.pagination.total
      });
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(activeTab);
  }, [activeTab]);

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }
    try {
      await blogService.deletePost(id);
      fetchPosts(activeTab, pagination.page);
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  };

  const handlePublishToggle = async (post: BlogPost) => {
    try {
      if (post.status === 'published') {
        await blogService.unpublishPost(post._id);
      } else {
        await blogService.publishPost(post._id);
      }
      fetchPosts(activeTab, pagination.page);
    } catch (err) {
      console.error('Error toggling publish status:', err);
      alert('Failed to update post status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Posts</h1>
        <Link to="/admin/posts/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>New Post
        </Link>
      </div>

      {/* Filter Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'story' ? 'active' : ''}`}
            onClick={() => handleTabChange('story')}
          >
            Stories
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => handleTabChange('news')}
          >
            News
          </button>
        </li>
      </ul>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted mb-3">No posts found.</p>
          <Link to="/admin/posts/new" className="btn btn-outline-primary">
            Create your first post
          </Link>
        </div>
      ) : (
        <>
          {/* Posts Table */}
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Featured</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post._id}>
                    <td>
                      <Link to={`/admin/posts/${post._id}/edit`} className="text-decoration-none">
                        {post.title}
                      </Link>
                    </td>
                    <td>
                      <span className={`badge ${post.postType === 'story' ? 'bg-info' : 'bg-warning'}`}>
                        {post.postType === 'story' ? 'Story' : 'News'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${post.status === 'published' ? 'bg-success' : 'bg-secondary'}`}>
                        {post.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      {post.featured && (
                        <i className="fas fa-star text-warning" title="Featured"></i>
                      )}
                    </td>
                    <td>{formatDate(post.publishedAt || post.updatedAt)}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => navigate(`/admin/posts/${post._id}/edit`)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className={`btn ${post.status === 'published' ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => handlePublishToggle(post)}
                        >
                          {post.status === 'published' ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(post._id, post.title)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <nav aria-label="Posts pagination">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => fetchPosts(activeTab, pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => fetchPosts(activeTab, pageNum)}>
                      {pageNum}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => fetchPosts(activeTab, pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default PostsListPage;
